/**
 * Mapea códigos de error de Firebase a mensajes amigables en español
 */
export const getFirebaseErrorMessage = (error: any): string => {
  if (!error) return 'Ocurrió un error inesperado. Por favor, intenta de nuevo.';

  const errorCode = error.code || '';
  const errorMessage = error.message || '';

  // Errores de autenticación
  const authErrors: Record<string, string> = {
    'auth/email-already-in-use': 'Este correo electrónico ya está registrado. Por favor, utiliza otro correo.',
    'auth/invalid-email': 'El correo electrónico no es válido. Verifica el formato.',
    'auth/invalid-credential': 'Credenciales incorrectas. Verifica tu usuario y contraseña.',
    'auth/user-not-found': 'No existe una cuenta con este correo electrónico.',
    'auth/wrong-password': 'La contraseña es incorrecta. Por favor, intenta de nuevo.',
    'auth/weak-password': 'La contraseña es muy débil. Debe tener al menos 6 caracteres.',
    'auth/too-many-requests': 'Demasiados intentos fallidos. Por favor, espera unos minutos e intenta de nuevo.',
    'auth/requires-recent-login': 'Por seguridad, debes volver a iniciar sesión para realizar esta acción.',
    'auth/operation-not-allowed': 'Esta operación no está permitida. Contacta al administrador.',
    'auth/user-disabled': 'Esta cuenta ha sido deshabilitada. Contacta al administrador.',
    'auth/network-request-failed': 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.',
  };

  // Errores de Firestore
  const firestoreErrors: Record<string, string> = {
    'permission-denied': 'No tienes permisos para realizar esta acción.',
    'not-found': 'El documento solicitado no existe.',
    'already-exists': 'Este registro ya existe en la base de datos.',
    'resource-exhausted': 'Se ha excedido el límite de solicitudes. Por favor, intenta más tarde.',
    'failed-precondition': 'No se puede completar la operación. Verifica que todos los datos sean correctos.',
    'aborted': 'La operación fue cancelada. Por favor, intenta de nuevo.',
    'out-of-range': 'Los datos proporcionados están fuera del rango permitido.',
    'unimplemented': 'Esta función no está disponible en este momento.',
    'internal': 'Error interno del servidor. Por favor, intenta más tarde.',
    'unavailable': 'El servicio no está disponible temporalmente. Por favor, intenta más tarde.',
    'data-loss': 'Se perdieron datos durante la operación. Por favor, contacta al administrador.',
    'unauthenticated': 'Debes iniciar sesión para realizar esta acción.',
  };

  // Errores de Storage
  const storageErrors: Record<string, string> = {
    'storage/unauthorized': 'No tienes permisos para acceder a este archivo.',
    'storage/canceled': 'La operación fue cancelada.',
    'storage/unknown': 'Error desconocido al subir el archivo. Por favor, intenta de nuevo.',
    'storage/object-not-found': 'El archivo no fue encontrado.',
    'storage/bucket-not-found': 'Error de configuración de almacenamiento. Contacta al administrador.',
    'storage/project-not-found': 'Error de configuración del proyecto. Contacta al administrador.',
    'storage/quota-exceeded': 'Se ha excedido el límite de almacenamiento.',
    'storage/unauthenticated': 'Debes iniciar sesión para subir archivos.',
    'storage/retry-limit-exceeded': 'Se excedió el límite de intentos. Por favor, intenta más tarde.',
    'storage/invalid-checksum': 'El archivo está corrupto. Por favor, intenta subirlo de nuevo.',
  };

  // Buscar el mensaje correspondiente al código de error
  if (authErrors[errorCode]) {
    return authErrors[errorCode];
  }

  if (firestoreErrors[errorCode]) {
    return firestoreErrors[errorCode];
  }

  if (storageErrors[errorCode]) {
    return storageErrors[errorCode];
  }

  // Detectar patrones comunes en el mensaje de error
  if (errorMessage.includes('network') || errorMessage.includes('Network')) {
    return 'Error de conexión. Verifica tu conexión a internet e intenta de nuevo.';
  }

  if (errorMessage.includes('timeout') || errorMessage.includes('Timeout')) {
    return 'La operación tardó demasiado tiempo. Por favor, intenta de nuevo.';
  }

  if (errorMessage.includes('permission') || errorMessage.includes('Permission')) {
    return 'No tienes permisos para realizar esta acción.';
  }

  if (errorMessage.includes('duplicate') || errorMessage.includes('already exists')) {
    return 'Este registro ya existe. Por favor, verifica los datos ingresados.';
  }

  // Mensaje genérico si no se encuentra un mapeo específico
  return 'Ocurrió un error inesperado. Por favor, intenta de nuevo o contacta al administrador si el problema persiste.';
};

/**
 * Obtiene un mensaje de error amigable para operaciones CRUD
 */
export const getCRUDErrorMessage = (operation: 'crear' | 'actualizar' | 'eliminar' | 'obtener', entity: string, error?: any): string => {
  const baseMessages = {
    crear: `No se pudo crear ${entity}.`,
    actualizar: `No se pudo actualizar ${entity}.`,
    eliminar: `No se pudo eliminar ${entity}.`,
    obtener: `No se pudo obtener la información de ${entity}.`
  };

  const baseMessage = baseMessages[operation];

  if (error) {
    const specificError = getFirebaseErrorMessage(error);
    return `${baseMessage} ${specificError}`;
  }

  return `${baseMessage} Por favor, intenta de nuevo.`;
};
